import { useEffect, useRef, useState } from 'react';
import { Smiley } from '@phosphor-icons/react';

// Curated, categorized emoji set — lightweight (no external library).
const EMOJI_CATEGORIES = [
  { id: 'smileys', label: '顔', emojis: ['😀', '😄', '😁', '😆', '😅', '😂', '🙂', '😉', '😊', '😍', '😘', '😎', '🤔', '😐', '😴', '😇', '🥳', '😭', '😢', '😡', '😱', '😳', '🤗', '🤩', '😋', '😜', '🤯', '😬'] },
  { id: 'gestures', label: '手', emojis: ['👍', '👎', '👌', '✌️', '🤝', '🙏', '👏', '🙌', '💪', '👋', '🤙', '✋', '🫶', '👆', '👉', '🫡'] },
  { id: 'hearts', label: '心', emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💯', '🔥', '✨', '⭐', '🌟', '💫', '🎉'] },
  { id: 'objects', label: '物', emojis: ['✅', '❌', '⚠️', '📌', '📎', '📝', '📅', '⏰', '💡', '📞', '💻', '🖥️', '📧', '📦', '🔧', '🛠️'] },
  { id: 'nature', label: '自然', emojis: ['🌸', '🌼', '🌿', '🌞', '🌙', '☔', '❄️', '🐱', '🐶', '🍀', '🌈', '🌊', '🍵', '🍙', '🍰', '☕'] },
];

/**
 * Emoji picker button + slide-up panel. Extracted from Composer to keep that
 * component focused on message-composition logic. Click-away closes; parent
 * only sees `onPick(emoji)` (fire-and-forget).
 */
export default function EmojiPicker({ onPick, testIdPrefix = 'emoji' }) {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(EMOJI_CATEGORIES[0].id);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const emojis = EMOJI_CATEGORIES.find((c) => c.id === cat)?.emojis || [];

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`chat-${testIdPrefix}-btn`}
        aria-label="絵文字を挿入"
        className={`w-9 h-9 inline-flex items-center justify-center rounded-lg active:scale-90 transition-all ${open ? 'bg-zinc-100 text-teal-700' : 'text-zinc-500 hover:bg-zinc-100'}`}
      >
        <Smiley size={19} weight={open ? 'fill' : 'regular'} />
      </button>
      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-zinc-200 rounded-xl shadow-lg overflow-hidden z-30"
          data-testid={`${testIdPrefix}-picker`}
        >
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-zinc-100 overflow-x-auto">
            {EMOJI_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                data-testid={`${testIdPrefix}-cat-${c.id}`}
                className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${cat === c.id ? 'bg-teal-700 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="p-2 grid grid-cols-8 gap-0.5 max-h-44 overflow-y-auto">
            {emojis.map((e, i) => (
              <button
                key={`${e}-${i}`}
                type="button"
                onClick={() => { onPick(e); setOpen(false); }}
                data-testid={`${testIdPrefix}-${e}`}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-lg hover:bg-zinc-100 active:scale-90 transition-all"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
